import { FlexContainer } from '@/ui/styled-ui';
import { Tooltip } from 'primereact/tooltip';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledResultCard = styled(FlexContainer)`
  background-color: var(--white-main);
  border-radius: var(--medium-border-radius);
  padding: var(--default-padding);
  gap: var(--default-padding);
  margin: 0;

  .question-container {
    align-self: flex-start;
  }

  .question-header {
    font-size: var(--xsmall-font-size);
    font-weight: 700;
    margin-bottom: var(--small-padding);
  }

  .question-text {
    font-size: var(--medium-font-size);
    line-height: var(--small-line-height);
  }
`;

const ChartContainer = styled(FlexContainer)`
  position: relative;
`;

const ChartArea = styled(FlexContainer)`
  position: relative;
  padding: var(--small-padding) 0;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 1px;
    background-color: var(--gray-dark);
    z-index: 1;
  }
`;

const BarRow = styled(FlexContainer)`
  gap: var(--small-padding);
  align-items: center;
  margin-bottom: var(--medium-padding);
`;

const BarContainer = styled.div`
  position: relative;
  flex: 1;
  height: 39px;
  overflow: hidden;
`;

const BarSegment = styled.div<{
  width: number;
  backgroundColor: string;
  color: string;
}>`
  height: 100%;
  width: ${({ width }) => width ?? 0}%;
  background-color: ${({ backgroundColor }) => backgroundColor};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: var(--heading-small-font-size);
  font-weight: 700;
  color: ${({ color }) => color};
  min-width: ${({ width }) => (width > 15 ? 'auto' : '44px')};
  padding: 0 var(--xsmall-padding) 0 var(--xxsmall-padding);
`;

const XAxisScale = styled(FlexContainer)`
  justify-content: space-between;
  font-size: var(--xsmall-font-size);
  color: var(--gray-moderate);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0px;
    left: 0;
    right: 0;
    height: 1px;
    background-color: var(--gray-moderate);
  }
`;

const XAxisTick = styled.div`
  position: relative;
  margin-top: var(--xxsmall-padding);
  font-size: var(--xsmall-font-size);
  color: var(--black-main);
`;

const Legend = styled(FlexContainer)`
  gap: var(--medium-padding);
  justify-content: flex-start;
`;

const LegendItem = styled(FlexContainer)`
  justify-content: flex-start;
  align-items: flex-start;
  gap: var(--xsmall-padding);
  font-size: var(--small-font-size);
  font-weight: 500;
  width: auto;
`;

const LegendColor = styled.div<{ color: string }>`
  width: 16px;
  min-width: 16px;
  height: 16px;
  min-height: 16px;
  background-color: ${({ color }) => color};
  border-radius: 50%;
`;

type ResultCardProps = {
  questionNumber?: number;
  questionText?: string;
  firstAttemptPercentage?: number;
  subsequentAttemptsPercentage?: number;
  totalEnrolledUsers?: number;
  totalSubsequentUsers?: number;
};

export const ResultCard: React.FC<ResultCardProps> = ({
  questionNumber,
  questionText,
  firstAttemptPercentage,
  subsequentAttemptsPercentage,
  totalEnrolledUsers,
  totalSubsequentUsers,
}) => {
  const { t } = useTranslation();
  return (
    <StyledResultCard id={`result-card-${questionNumber}`} direction="column">
      <div className="question-container">
        <div className="question-header">
          {t('results.questionNumber', { number: questionNumber })}:
        </div>
        <div className="question-text">{questionText}</div>
      </div>

      <ChartContainer direction="column">
        <ChartArea direction="column">
          <BarRow direction="row">
            <BarContainer>
              <BarSegment
                id={`first-attempt-bar-${questionNumber}`}
                width={firstAttemptPercentage ?? 0}
                backgroundColor="var(--gray-dark)"
                color="var(--purple-middle)"
              >
                {firstAttemptPercentage}%
              </BarSegment>
            </BarContainer>
          </BarRow>

          <BarRow direction="row">
            <BarContainer>
              <BarSegment
                id={`subsequent-attempts-bar-${questionNumber}`}
                width={subsequentAttemptsPercentage ?? 0}
                backgroundColor="var(--red-main)"
                color="var(--white-main)"
              >
                {subsequentAttemptsPercentage}%
              </BarSegment>
            </BarContainer>
          </BarRow>
        </ChartArea>

        <XAxisScale direction="row">
          <XAxisTick>0%</XAxisTick>
          <XAxisTick>50%</XAxisTick>
          <XAxisTick>100%</XAxisTick>
        </XAxisScale>
      </ChartContainer>

      <Tooltip
        target={`#first-attempt-bar-${questionNumber}`}
        content={t('results.percentageTooltip', {
          totalUsers: totalEnrolledUsers,
        })}
        position="top"
        my="center bottom-5"
      />

      <Tooltip
        target={`#subsequent-attempts-bar-${questionNumber}`}
        content={t('results.percentageTooltip', {
          totalUsers: totalSubsequentUsers,
        })}
        position="top"
        my="center bottom-5"
      />

      <Legend direction="row">
        <LegendItem direction="row">
          <LegendColor color="var(--gray-dark)" />
          <span>{t('results.firstAttempt')}</span>
        </LegendItem>
        <LegendItem direction="row">
          <LegendColor color="var(--red-main)" />
          <span>{t('results.finalAttempt')}</span>
        </LegendItem>
      </Legend>
    </StyledResultCard>
  );
};
